import get from 'lodash/get';
import { getEngineDescription } from '../utils/engine-description.util';
import {
  BFSProductConfigurationDTO,
  DCPBadgeDTO,
  DCPCarProductEquipmentDTO,
  DCPColorVariantsDTO,
  DCPEquipmentDTO,
  DCPFinancialInformationDTO,
  DCPPictogramDTO,
  DCPProductInstructionDTO,
  DCPProductTaggedAttributeDTO,
  DCPProductTypeDTO,
  DCPProviderIdentificationDTO,
  DCPTileMediaDTO,
  DCPValueUnitData,
  DCPValueUnitDTO,
  DCPVehicleConfigurationProductDetailDataDTO,
  DCPVehiclePriceInformationDTO,
  DCPVehicleProductConfigurationDTO,
  OVSPaymentModeDTO,
  SMSBaseOption,
  SMSCategory,
  SMSClassification,
  SMSDeliveryMode,
  SMSEnumDTO,
  SMSFeature,
  SMSFutureStock,
  SMSImage,
  SMSPointOfService,
  SMSPrice,
  SMSPriceRange,
  SMSProduct,
  SMSProductReference,
  SMSPromotion,
  SMSReview,
  SMSSeoDTO,
  SMSStock,
  SMSVariantMatrixElement,
  SMSVariantOption,
} from '@dh-io-emh/commerce-api/src/models/shared/ocb/api.schema';
import { infuse } from '../ioc-config';
import { jsonParse } from '../utils/json-parse.util';
import { getTranslationUnitFallback, translateProductEnums } from '../utils/translation.utils';

export interface IEmicoData {
  id: string;
  displayValue: string;
  displayImage?: string;
  label: string;
  footnoteText: string;
  mustshowin: Array<'SHORTOVERVIEW' | 'DETAILVIEW'>;
  order: number;
}

export enum BodyTypeCode {
  ALL_TERRAIN = 'ALL_TERRAIN',
  CABRIO = 'CABRIO',
  CHASSIS = 'CHASSIS',
  CHASSIS_CREW_CAB = 'CHASSIS_CREW_CAB',
  COUPE = 'COUPE',
  COMPACT = 'COMPACT',
  LIMOUSINE = 'LIMOUSINE',
  LONG_SEDAN = 'LONG_SEDAN',
  MARCO_POLO = 'MARCO_POLO',
  MIXTO = 'MIXTO',
  HATCHBACK = 'HATCHBACK',
  OFF_ROADER = 'OFF_ROADER',
  PICKUP = 'PICKUP',
  ROADSTER = 'ROADSTER',
  SHOOTING_BRAKE = 'SHOOTING_BRAKE',
  SPORTS_TOURER = 'SPORTS_TOURER',
  STATION = 'STATION',
  SUV = 'SUV',
  T_MODEL = 'T_MODEL',
  VAN = 'VAN',
  PANEL_VAN = 'PANEL_VAN',
  TOURER = 'TOURER',
  PEOPLE_CARRIER = 'PEOPLE_CARRIER',
  PLATFORM = 'PLATFORM',
  PLATFORM_CREW_CAB = 'PLATFORM_CREW_CAB',
}

export enum FuelTypeCode {
  DIESEL = 'DIESEL',
  DIESEL_ELECTRIC_PLUGIN_HYBRID = 'DIESEL_ELECTRIC_PLUGIN_HYBRID',
  PETROL_ELECTRIC_PLUGIN_HYBRID = 'PETROL_ELECTRIC_PLUGIN_HYBRID',
  ELECTRIC = 'ELECTRIC',
  PETROL = 'PETROL',
}

export enum StockType {
  IN_STOCK = 'IN_STOCK',
  COMING_SOON = 'COMING_SOON',
}

export enum VEHICLE_IMAGE_TYPE {
  EXTERIOR = 'EXTERIOR',
  INTERIOR = 'INTERIOR',
}

export enum CommercialVansDcpType {
  NCOS_B_COMVANS = 'NCOS_B_COMVANS',
  NCOS_D_COMVANS = 'NCOS_D_COMVANS',
}

export enum PrivateVehicleDcpType {
  NCOS_D_STANDARD = 'NCOS_D_STANDARD',
  NCOS_B_STANDARD = 'NCOS_B_STANDARD',
}

export enum DivisionCode {
  VAN = 'VAN',
  PC = 'PC',
}

export enum DivisionOverseasCode {
  Vans = 'Vans',
  Cars = 'Cars',
}

export interface ExtendedSMSProduct extends SMSProduct {
  powerTranslation?: string;
}

export default class Vehicle implements ExtendedSMSProduct {
  acceleration: DCPValueUnitDTO;
  accelerationUnit: string;
  accelerationValue: number;
  activeVariant: string;
  additionalValues?: { rawRate: string; type: string }[];
  allWheelDrive: boolean;
  assortment: string;
  availabilityStatus: string;
  availableForPickup: boolean;
  averageRating: number;
  b2bFilterPrice: DCPFinancialInformationDTO;
  b2bInstalmentRate: DCPFinancialInformationDTO;
  b2cFilterPrice: DCPFinancialInformationDTO;
  b2cInstalmentRate: DCPFinancialInformationDTO;
  badge: string;
  badges: Required<DCPBadgeDTO>[];
  baseId: string;
  baseOptions: SMSBaseOption[];
  baseProcessId: string;
  baseProduct: string;
  baumuster: string;
  bfsProductConfiguration: BFSProductConfigurationDTO;
  bodyType: SMSEnumDTO;
  brand: string;
  brandName: string;
  carEquipments: Required<DCPCarProductEquipmentDTO>[];
  careInstructions: DCPProductInstructionDTO[];
  categories: Required<SMSCategory>[];
  categoryId: string;
  classifications: Required<SMSClassification>[];
  co2EmissionMaxUnit: string;
  co2EmissionMaxValue: number;
  co2EmissionMinUnit: string;
  co2EmissionMinValue: number;
  code: string;
  color: string;
  colorCode: string;
  colorGroup: string;
  colorGroupCode: string;
  colorPolish: string;
  colorText: string;
  colorUrl: string;
  colorVariants: DCPColorVariantsDTO[];
  combinedPower: DCPValueUnitDTO;
  combinedPowerSecondary: DCPValueUnitDTO;
  combustionEngineSize: DCPValueUnitDTO;
  combustionPower: DCPValueUnitDTO;
  combustionPowerSecondary: DCPValueUnitDTO;
  commissionNumber: string;
  configurable: boolean;
  configuratorType: string;
  consignorCompanyDisplayName: string;
  consumptionCityMaxUnit: string;
  consumptionCityMaxValue: number;
  consumptionCityMinUnit: string;
  consumptionCityMinValue: number;
  consumptionCombinedMaxUnit: string;
  consumptionCombinedMaxValue: number;
  consumptionCombinedMinUnit: string;
  consumptionCombinedMinValue: number;
  consumptionOverlandMaxUnit: string;
  consumptionOverlandMaxValue: number;
  consumptionOverlandMinUnit: string;
  consumptionOverlandMinValue: number;
  cylinder: number;
  cylinderCapacityUnit: string;
  cylinderCapacityValue: number;
  damageInfo: string;
  dcpProductType: Required<DCPProductTypeDTO>;
  defaultCategory: Required<SMSCategory>;
  deliveryModes: Required<SMSDeliveryMode>[];
  description: string;
  discountPrice: SMSPrice;
  division: string;
  doors: number;
  driveType: SMSEnumDTO;
  duration: string;
  earliestRenewableDate: string;
  earliestRenewableDuration: string;
  ecotax: DCPFinancialInformationDTO;
  electricPower: DCPValueUnitDTO;
  electricPowerSecondary: DCPValueUnitDTO;
  emicoData?: IEmicoData[];
  emicoDataWltp?: IEmicoData[];
  energyEfficiencyClass: string;
  energyEfficiencyClassImages: SMSImage[];
  energyLabelUrl: string;
  engineSize: DCPValueUnitDTO;
  engineSizeInCcm: number;
  engineType: string;
  equipment: DCPEquipmentDTO[];
  externalPdp: string;
  externalReferenceId: string;
  extraInformation: string;
  faq: string;
  financialCode: string;
  firstRegistrationDate: string;
  fiscalValue: Required<DCPFinancialInformationDTO>;
  fomLink: string;
  frontTyreSize: string;
  fuelEfficiency: string;
  fuelTankCapacity: DCPValueUnitDTO;
  fuelType: SMSEnumDTO;
  futureStocks: SMSFutureStock[];
  gearBox: SMSEnumDTO;
  gross: number;
  hasPreviousDamage: boolean;
  hasTrialVariant: boolean;
  hazardPictograms: DCPPictogramDTO[];
  height: DCPValueUnitDTO;
  highlight1: string;
  highlight2: string;
  highlight3: string;
  highlights: string[];
  imageIcon: string;
  images: Required<SMSImage>[];
  importantAttributes: DCPProductTaggedAttributeDTO[];
  installationGuides: DCPProductInstructionDTO[];
  lastBuyDate: string;
  length: DCPValueUnitDTO;
  licenseExpiredDate: string;
  listPrice: Required<SMSPrice>;
  loadingSpaceHeight: DCPValueUnitDTO;
  loadingSpaceLength: DCPValueUnitDTO;
  loadingSpaceVolume: DCPValueUnitDTO;
  loadingSpaceWidth: DCPValueUnitDTO;
  localEmissionAndConsumptionData: string;
  manufacturer: string;
  markets: string[];
  materialSafetyDataSheet: string;
  maxLoad: DCPValueUnitDTO;
  maxSpeed: DCPValueUnitDTO;
  maxWeight: DCPValueUnitDTO;
  mbCertification: string;
  mileage: DCPValueUnitDTO;
  model: string;
  modelDesignation: string;
  modelNo: string;
  modelYear: string;
  modelYearCode: string;
  monthlyRate: Required<DCPFinancialInformationDTO>;
  multidimensional: boolean;
  name: string;
  nationalType: string;
  noiseLevel: DCPValueUnitDTO;
  numberOfCylinders: number;
  numberOfDoors: number;
  numberOfGears: number;
  numberOfReviews: number;
  numberOfSeats: number;
  numberPreviousOwners: number;
  orderNumber: string;
  paymentModes: OVSPaymentModeDTO[];
  pointOfServiceCode: string;
  pointOfServiceDisplayName: string;
  pointOfServiceFormattedAddress: string;
  pointOfServices: SMSPointOfService[];
  potentialPromotions: SMSPromotion[];
  powerHpUnit: string;
  powerHpValue: number;
  powerInKw: number;
  powerKwUnit: string;
  powerKwValue: number;
  price: Required<SMSPrice>;
  priceRange: SMSPriceRange;
  priceWithPrefix: string;
  primaryImage: SMSImage;
  priority: number;
  productNameEn: string;
  productPrices: SMSPrice[];
  productReferences: SMSProductReference[];
  productRequestRequired: boolean;
  productTeaserImage: string;
  productType: string;
  propertiesLegal: string;
  providerIdentification: DCPProviderIdentificationDTO;
  purchasable: boolean;
  rearTyreSize: string;
  recyclingAdviceList: DCPProductInstructionDTO[];
  reducedPrice: string;
  reservationFee: SMSPrice;
  reviews: SMSReview[];
  rollNoise: string;
  runFlat: boolean;
  runtimeEndDate: string;
  salesClassCode: string;
  salesClassText: string;
  season: string;
  seats: number;
  seo: SMSSeoDTO;
  serviceType: string;
  shortDescription: string;
  sizeTableUrl: string;
  snowFlake: boolean;
  softCompound: boolean;
  source: string;
  steeringWheel: SMSEnumDTO;
  stock: SMSStock;
  stockCategory: string;
  stockId: string;
  studded: boolean;
  summary: string;
  tags: string[];
  techRequirements: string;
  threeSixtyImages: SMSImage[];
  thumbnailImage: SMSImage;
  tileMediaList: DCPTileMediaDTO[];
  topSpeedUnit: string;
  topSpeedValue: number;
  torque: DCPValueUnitDTO;
  totalCondition: string;
  transmission: string;
  tyreTread: string;
  upholstery: string;
  upholsteryCode: string;
  upholsteryGroup: string;
  upholsteryGroupCode: string;
  upholsteryPolish: string;
  upholsteryText: string;
  upholsteryUrl: string;
  url: string;
  usage: string;
  userManuals: DCPProductInstructionDTO[];
  variantId: string;
  variantMatrix: SMSVariantMatrixElement[];
  variantOptions: SMSVariantOption[];
  variantType: string;
  vatReclaimable: boolean;
  vehicleConfigurationProductDetail: DCPVehicleConfigurationProductDetailDataDTO;
  vehicleId: string;
  vehiclePriceInformation: DCPVehiclePriceInformationDTO;
  vehicleProductConfiguration: DCPVehicleProductConfigurationDTO;
  vehicleSearchUrl: string;
  vin: string;
  volume: DCPValueUnitData;
  volumePrices: SMSPrice[];
  volumePricesFlag: boolean;
  warrantyDuration: DCPValueUnitDTO;
  warrantyName: string;
  weight: DCPValueUnitDTO;
  wetGrip: string;
  wholeSale: boolean;
  width: DCPValueUnitDTO;
  powerTranslation: string;

  private static mapEmicoData(emicoData: any, lang: string): IEmicoData[] {
    const footnote = emicoData?.footnotes?.[lang] as string;

    return (emicoData?.attributes ?? [])
      .map(
        (item: any) =>
          ({
            id: item.id,
            displayValue: item.displayValue,
            ...(item.displayImage && { displayImage: item.displayImage[lang] }),
            label: item.label && item.label[lang] ? item.label[lang] : '',
            footnoteText: footnote,
            order: item.order ?? 0,
            mustshowin: item.mustshowin ?? ['SHORTOVERVIEW', 'DETAILVIEW'],
          } as IEmicoData),
      )
      .sort((a, b) => a.order - b.order);
  }

  static create(data: ExtendedSMSProduct) {
    const { lang } = infuse('config');

    const emicoParsed = jsonParse(data.localEmissionAndConsumptionData);

    const emicoData = {
      nefz: this.mapEmicoData(emicoParsed?.uidata?.overlay, lang),
      wltp: this.mapEmicoData(emicoParsed?.uidata, lang),
    };

    const valueFormat = (name: string): any => {
      if (!data[name] || data[name]?.value === 0) return null;

      const displayName = data[name]?.displayValue
        ? data[name]?.displayValue
        : data[name]?.value + ' ' + data[name]?.unit;

      return getTranslationUnitFallback(data[name]?.value, `dcp.unit.${data[name].unitCode}`, displayName);
    };

    const getClassificationFeatureValue = (name: string) =>
      (
        get(data, 'classifications[0].features')?.find((classification) =>
          classification.code?.endsWith(name),
        ) as Required<SMSFeature>
      )?.featureValues[0].value;

    const sourceData = {
      assortment: 'vehicle',
      driveType: data.driveType?.code,
      emicoData: emicoData.nefz,
      emicoDataWltp: emicoData.wltp,
      powerTranslation:
        data.powerTranslation ||
        getEngineDescription(
          data.combustionPower ?? {},
          data.combustionPowerSecondary ?? {},
          data.electricPower ?? {},
          data.electricPowerSecondary ?? {},
        ),
      maxLoad: valueFormat('maxLoad'),
      maxSpeed: valueFormat('maxSpeed'),
      maxWeight: valueFormat('maxWeight'),
      torque: valueFormat('torque'),
      loadingSpaceLength: valueFormat('loadingSpaceLength'),
      loadingSpaceWidth: valueFormat('loadingSpaceWidth'),
      loadingSpaceHeight: valueFormat('loadingSpaceHeight'),
      loadingSpaceVolume: valueFormat('loadingSpaceVolume'),
      weight: valueFormat('weight'),
      width: valueFormat('width'),
      height: valueFormat('height'),
      engineSizeInCcm: valueFormat('engineSize'),
      mileage: valueFormat('mileage'),
      acceleration: valueFormat('acceleration'),
      images: [...[get(data, 'primaryImage')], ...get(data, 'images', [])],
      highlights: [
        get(data, 'highlight1', false),
        get(data, 'highlight2', false),
        get(data, 'highlight3', false),
      ].filter(Boolean),
      length: valueFormat('length'),
      combustionEngineSize: valueFormat('length'),
      fuelTankCapacity: valueFormat('fuelTankCapacity'),
      fuelType: data.fuelType ?? getClassificationFeatureValue('.fuel_type'),
      color: data.colorText ?? getClassificationFeatureValue('.color_text'),
      upholstery: data.upholsteryText ?? getClassificationFeatureValue('.upholstery_text'),
      listPrice: data.listPrice || {},
      transmission: data.gearBox?.name || data.transmission,
      productType: data.productType ?? data.dcpProductType?.name,
      vehicleSearchUrl: get(data, 'bfsProductConfiguration.bfsDetailUrl'),
      summary: (() => {
        let summary = '';

        if (data.powerInKw) {
          summary += `${data.powerInKw} kW (${Math.round(Number(data.powerInKw) * 1.36)} hp),`;
        }

        if (data.fuelType) {
          summary += ` ${data.fuelType},`;
        }

        if (data.transmission) {
          summary += ` ${data.transmission}`;
        }

        if (data.summary?.substr(-1, 1) === ',') {
          summary = summary.substr(0, summary.length - 1);
        }

        return summary;
      })(),
      consignorCompanyDisplayName: data.consignorCompanyId,
    } as Partial<Vehicle>;

    return translateProductEnums(Object.assign(new Vehicle(), data, sourceData));
  }
}
